import { SOUND_LIBRARY, type SoundName } from '../types';
import type { UIMode } from '../context/UIModeContext';

/**
 * SoundPicker - Sound selection with freemium gating
 *
 * Features:
 * - Button grid with icons
 * - Visual sound descriptions
 * - Feature gating for Pro sounds
 * - Simple mode: 2 sounds (beep, tap) - single selection
 * - Advanced mode: All 11 sounds - dual selection for mixing (beep + continuous)
 */

interface SoundPickerProps {
  value: SoundName;              // For single-select mode (simple mode)
  values?: SoundName[];          // For multi-select mode (advanced mode)
  onChange: (sound: SoundName) => void;
  multiSelect?: boolean;         // Enable multi-select behavior
  disabled?: boolean;
  isPro: boolean;
  mode: UIMode;
}

// Simple mode sounds - includes "off" as first option
const SIMPLE_MODE_SOUNDS: SoundName[] = ['off', 'tap', 'beep', 'heartbeat'];

// Icon mapping for each sound
const SOUND_ICONS: Record<SoundName, string> = {
  // No sound
  off: '🔇',

  // Discrete beep sounds
  tap: '👆',
  beep: '📢',
  heartbeat: '💓',
  rain: '💧',
  snap: '🫰',
  wood: '🪵',
  bamboo: '🎋',
  drum:'🥁',
  tibetan_ping:'🔔',

  // Continuous looping sounds
  ocean: '🌊',
  ambient: '✨',
  angelic: '👼',
  handpan: '🎵',
  underwater: '🐠',
  drone: '🎚️',
};

export function SoundPicker({
  value,
  values = [],
  onChange,
  multiSelect = false,
  disabled = false,
  isPro,
  mode,
}: SoundPickerProps): JSX.Element {
  // Simple mode: show 4 sounds including "off", single selection
  if (mode === 'simple') {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Sound
        </label>

        <div className="grid grid-cols-4 gap-2">
          {SIMPLE_MODE_SOUNDS.map((soundName) => {
            const soundData = SOUND_LIBRARY[soundName];
            const isLocked = soundData.isPro && !isPro;
            const isSelected = value === soundName;

            return (
              <button
                key={soundName}
                type="button"
                onClick={() => !isLocked && !disabled && onChange(soundName)}
                disabled={disabled || isLocked}
                className={`
                  relative p-2 rounded-lg border-2 transition-all
                  ${isSelected ? 'border-sync-blue bg-sync-blue/10' : 'border-gray-300 bg-white'}
                  ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:border-sync-blue cursor-pointer'}
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                title={soundData.description}
              >
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-xl">{SOUND_ICONS[soundName]}</span>
                  <span className="text-[10px] font-medium text-gray-700">
                    {soundData.displayName}
                  </span>
                </div>
                {isLocked && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Advanced mode: separate beep sounds from continuous sounds
  const allSounds = Object.entries(SOUND_LIBRARY).sort((a, b) => a[1].order - b[1].order);

  // Continuous sounds (looping sounds with smooth panning)
  const continuousSoundNames: SoundName[] = ['ocean', 'ambient', 'angelic', 'handpan', 'underwater', 'drone'];
  // Exclude 'off' from advanced mode - use the audio toggle instead
  const beepSounds = allSounds.filter(([name]) => name !== 'off' && !continuousSoundNames.includes(name as SoundName));
  const continuousSounds = allSounds.filter(([name]) => continuousSoundNames.includes(name as SoundName));

  const renderSoundButton = (
    soundName: string,
    soundData: typeof SOUND_LIBRARY[SoundName],
    isSelected: boolean,
    onClick: () => void
  ) => {
    const isLocked = soundData.isPro && !isPro;

    return (
      <button
        key={soundName}
        type="button"
        onClick={() => !isLocked && !disabled && onClick()}
        disabled={disabled || isLocked}
        className={`
          relative p-2 rounded-lg border-2 transition-all
          ${isSelected ? 'border-sync-blue bg-sync-blue/10' : 'border-gray-300 bg-white'}
          ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:border-sync-blue cursor-pointer'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        title={soundData.description}
      >
        <div className="flex flex-col items-center gap-1">
          <span className="text-xl">{SOUND_ICONS[soundName as SoundName]}</span>
          <span className="text-xs font-medium text-gray-700">
            {soundData.displayName}
          </span>
        </div>
        {isLocked && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* Beep Sounds Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Beep Sounds
        </label>
        <div className="grid grid-cols-3 gap-2">
          {beepSounds.map(([soundName, soundData]) =>
            renderSoundButton(
              soundName,
              soundData,
              multiSelect ? values.includes(soundName as SoundName) : value === soundName,
              () => onChange(soundName as SoundName)
            )
          )}
        </div>
      </div>

      {/* Continuous Sounds Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Continuous Sounds
        </label>
        <div className="grid grid-cols-3 gap-2">
          {continuousSounds.map(([soundName, soundData]) =>
            renderSoundButton(
              soundName,
              soundData,
              multiSelect ? values.includes(soundName as SoundName) : value === soundName,
              () => onChange(soundName as SoundName)
            )
          )}
        </div>
      </div>

      {!isPro && (
        <div className="text-xs text-gray-500 text-center pt-1">
          <span className="pro-icon inline-block mr-1"></span> Upgrade to Pro for all sounds and mixing
        </div>
      )}
    </div>
  );
}
